import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api/ApplicationApi';
import { BaseDialog } from 'library/components/controls/dialogs/BaseDialog';
import {
	InlineDisplayTextContainer,
	InlineDisplayValue,
} from 'library/components/controls/display/InlineDisplayValue';

import { ServiceContract } from './types';

export type SuspendReactivateServiceContractDialogProps = {
	serviceContract: ServiceContract;
	onClose: () => void;
	onConfirm: () => void;
};

export const SuspendReactivateServiceContractDialog = ({
	serviceContract,
	onClose,
	onConfirm,
}: SuspendReactivateServiceContractDialogProps) => {
	const { api } = useProductCenterApi();
	const { t } = useTranslation(['serviceContracts', 'commonButton']);
	const { enqueueSnackbar } = useSnackbar();

	const isSuspended =
		serviceContract.status === 'Paused' ||
		serviceContract.status === 'Suspended';

	const title = isSuspended
		? t('serviceContracts:reactivate-service-contract-title')
		: t('serviceContracts:suspend-service-contract-title');

	const description = isSuspended
		? t('serviceContracts:reactivate-service-contract-description')
		: t('serviceContracts:suspend-service-contract-description');

	const handleOnConfirm = useCallback(async () => {
		if (!serviceContract.id) {
			enqueueSnackbar(
				t('serviceContracts:status-change-failure-snackbar'),
				{
					variant: 'error',
				}
			);
			return;
		}

		try {
			await api.changeServiceContractStatus(
				serviceContract.id,
				isSuspended ? 'Active' : 'Paused'
			);
		} catch {
			enqueueSnackbar(
				t('serviceContracts:status-change-failure-snackbar'),
				{
					variant: 'error',
				}
			);
			return;
		}

		enqueueSnackbar(
			isSuspended
				? t('serviceContracts:reactivate-success-snackbar')
				: t('serviceContracts:suspend-success-snackbar')
		);
		onConfirm();
		onClose();
	}, [
		api,
		enqueueSnackbar,
		isSuspended,
		serviceContract.id,
		onConfirm,
		onClose,
		t,
	]);

	return (
		<BaseDialog
			title={title}
			onClose={onClose}
			onConfirm={handleOnConfirm}
			confirmButtonText={t('commonButton:yes')}
			closeButtonText={t('commonButton:no')}>
			<Typography sx={{ mt: 2, mb: 4 }}>{description}</Typography>
			<InlineDisplayTextContainer columnsCount={1} rowsCount={2}>
				<InlineDisplayValue
					label={t('serviceContracts:service-contract')}
					value={serviceContract.id}
				/>
				<InlineDisplayValue
					label={t('serviceContracts:status')}
					value={serviceContract.status}
				/>
			</InlineDisplayTextContainer>
		</BaseDialog>
	);
};
